import React, { useEffect, useState } from 'react';
import { Building2, Star, DollarSign, Users, ChevronDown, ChevronUp, CheckCircle, XCircle, Shield } from 'lucide-react';
import { adminAPI } from '@/services/api';

export default function AdminOrgPortfolio() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    const load = async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/organizations`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        });
        const d = await r.json();
        setOrgs(d.organizations || []);
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
  }, []);

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const handleApproval = async (orgId, approved) => {
    setActionLoading(prev => ({ ...prev, [orgId]: true }));
    try {
      await adminAPI.approveOrg(orgId, approved);
      setOrgs(prev => prev.map(o => o.id === orgId ? { ...o, org_approved: approved } : o));
    } catch (err) {
      alert(err?.response?.data?.detail || 'Action failed');
    }
    setActionLoading(prev => ({ ...prev, [orgId]: false }));
  };

  if (loading) return <p className="text-slate-500 dark:text-zinc-500">Loading organizations...</p>;

  return (
    <div data-testid="admin-org-portfolio">
      <h1 className="text-2xl font-semibold text-slate-900 dark:text-white mb-1">Organization Portfolio</h1>
      <p className="text-slate-500 dark:text-zinc-500 text-sm mb-6">Manage companies, approve for job posting, review salary data.</p>

      {orgs.length === 0 ? (
        <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-10 text-center">
          <Building2 className="w-10 h-10 text-slate-600 dark:text-zinc-700 mx-auto mb-3" />
          <p className="text-slate-500 dark:text-zinc-500">No organizations registered yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orgs.map(org => (
            <div key={org.id} className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg overflow-hidden" data-testid={`org-${org.id}`}>
              <button onClick={() => toggle(org.id)} className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-100 dark:hover:bg-zinc-800/20 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div className="text-left">
                    <div className="flex items-center gap-2">
                      <p className="text-slate-900 dark:text-white font-medium">{org.name}</p>
                      {org.org_approved ? (
                        <span className="text-[10px] px-1.5 py-0.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center gap-0.5" data-testid={`org-approved-${org.id}`}>
                          <CheckCircle className="w-3 h-3" /> Approved
                        </span>
                      ) : (
                        <span className="text-[10px] px-1.5 py-0.5 bg-amber-500/10 text-amber-600 dark:text-amber-400 rounded-full flex items-center gap-0.5" data-testid={`org-pending-${org.id}`}>
                          <Shield className="w-3 h-3" /> Pending
                        </span>
                      )}
                    </div>
                    <p className="text-slate-500 dark:text-zinc-500 text-xs">{org.domain} &middot; {org.total_jobs} jobs &middot; {org.active_jobs} active</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {org.avg_rating > 0 && (
                    <div className="flex items-center gap-1"><Star className="w-4 h-4 text-amber-600 dark:text-amber-400 fill-amber-400" /><span className="text-amber-600 dark:text-amber-400 text-sm font-medium">{org.avg_rating}</span><span className="text-slate-500 dark:text-zinc-600 text-xs">({org.total_reviews})</span></div>
                  )}
                  {expanded[org.id] ? <ChevronUp className="w-4 h-4 text-slate-500 dark:text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-slate-500 dark:text-zinc-500" />}
                </div>
              </button>

              {expanded[org.id] && (
                <div className="px-5 pb-5 border-t border-slate-200 dark:border-zinc-800/50 pt-4 space-y-5">
                  {/* Approval Actions */}
                  <div className="flex items-center gap-3">
                    <span className="text-slate-500 dark:text-zinc-400 text-sm">Job posting approval:</span>
                    {!org.org_approved ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleApproval(org.id, true); }}
                        disabled={actionLoading[org.id]}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 text-emerald-600 dark:text-emerald-400 rounded-lg text-xs hover:bg-emerald-500/20 transition-colors disabled:opacity-50"
                        data-testid={`approve-org-${org.id}`}>
                        <CheckCircle className="w-3.5 h-3.5" />
                        {actionLoading[org.id] ? 'Processing...' : 'Approve'}
                      </button>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleApproval(org.id, false); }}
                        disabled={actionLoading[org.id]}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 border border-red-500/30 text-red-600 dark:text-red-400 rounded-lg text-xs hover:bg-red-500/20 transition-colors disabled:opacity-50"
                        data-testid={`revoke-org-${org.id}`}>
                        <XCircle className="w-3.5 h-3.5" />
                        {actionLoading[org.id] ? 'Processing...' : 'Revoke Approval'}
                      </button>
                    )}
                  </div>

                  {/* Salary Ranges */}
                  {Object.keys(org.salary_ranges || {}).length > 0 && (
                    <div>
                      <h3 className="text-slate-900 dark:text-white text-sm font-medium mb-3 flex items-center gap-2"><DollarSign className="w-4 h-4 text-emerald-600 dark:text-emerald-400" /> Salary Ranges by Designation</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {Object.entries(org.salary_ranges).map(([title, range]) => (
                          <div key={title} className="bg-slate-50 dark:bg-zinc-900 rounded-lg px-3 py-2 flex items-center justify-between">
                            <span className="text-slate-600 dark:text-zinc-300 text-sm">{title}</span>
                            <span className="text-emerald-600 dark:text-emerald-400 text-sm font-medium">
                              {range.currency} {range.min?.toLocaleString()} - {range.max?.toLocaleString()}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Reviews */}
                  <div>
                    <h3 className="text-slate-900 dark:text-white text-sm font-medium mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-blue-600 dark:text-blue-400" /> Candidate Reviews ({org.total_reviews})</h3>
                    {org.reviews?.length > 0 ? (
                      <div className="space-y-2">
                        {org.reviews.map((r, i) => (
                          <div key={i} className="bg-slate-50 dark:bg-zinc-900 rounded-lg p-3">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="flex">{[1,2,3,4,5].map(s => <Star key={s} className={`w-3.5 h-3.5 ${s <= r.rating ? 'text-amber-600 dark:text-amber-400 fill-amber-400' : 'text-slate-600 dark:text-zinc-700'}`} />)}</div>
                              <span className="text-slate-500 dark:text-zinc-600 text-xs">{new Date(r.created_at).toLocaleDateString()}</span>
                              {r.would_recommend && <span className="text-xs px-1.5 py-0.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded">Recommends</span>}
                            </div>
                            {r.interview_experience && <p className="text-slate-500 dark:text-zinc-400 text-sm">{r.interview_experience}</p>}
                            {r.job_offer_feedback && <p className="text-slate-500 dark:text-zinc-500 text-xs mt-1 italic">{r.job_offer_feedback}</p>}
                          </div>
                        ))}
                      </div>
                    ) : <p className="text-slate-500 dark:text-zinc-600 text-sm">No reviews yet</p>}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
